import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Suspense, lazy, useEffect } from "react";
import { Loader2 } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import AccueilPage from "@/pages/AccueilPage";
import SiteFooter from "@/components/SiteFooter";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import BandeauCookies from "@/components/BandeauCookies";
import "@/App.css";

/* Chargement allégé : chaque page est chargée à la demande (React.lazy) —
   le visiteur public ne télécharge que la page visitée, jamais le code de
   l'espace interne ; chaque page interne est également livrée à part. */
const PreRegistrationForm = lazy(() => import("@/components/PreRegistrationForm"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const SouscriptionPage = lazy(() => import("@/pages/SouscriptionPage"));
const FaqPage = lazy(() => import("@/pages/FaqPage"));
const PolitiqueConfidentialitePage = lazy(() => import("@/pages/PolitiqueConfidentialitePage"));
const MentionsLegalesPage = lazy(() => import("@/pages/MentionsLegalesPage"));
const CgvPage = lazy(() => import("@/pages/CgvPage"));
const AgencesPage = lazy(() => import("@/pages/AgencesPage"));
const BibliothequePage = lazy(() => import("@/pages/BibliothequePage"));
const Login = lazy(() => import("@/pages/interne/Login"));
const InternalLayout = lazy(() => import("@/pages/interne/InternalLayout"));
const Dashboard = lazy(() => import("@/pages/interne/Dashboard"));
const CentreDemandesPage = lazy(() => import("@/pages/interne/CentreDemandesPage"));
const PreEnregistrementsPage = lazy(() => import("@/pages/interne/PreEnregistrementsPage"));
const SouscriptionsSuivi = lazy(() => import("@/pages/interne/SouscriptionsSuivi"));
const SouscriptionsAgencePage = lazy(() => import("@/pages/interne/SouscriptionsAgencePage"));
const SitesVentePage = lazy(() => import("@/pages/interne/SitesVentePage"));
const ContactsPage = lazy(() => import("@/pages/interne/ContactsPage"));
const ComptesClientelePage = lazy(() => import("@/pages/interne/ComptesClientelePage"));
const AgendaRdvClientelePage = lazy(() => import("@/pages/interne/AgendaRdvClientelePage"));
const CodesEnvoyesPage = lazy(() => import("@/pages/interne/CodesEnvoyesPage"));
const CodesPromoPage = lazy(() => import("@/pages/interne/CodesPromoPage"));
const UtilisateursPage = lazy(() => import("@/pages/interne/UtilisateursPage"));
const PilotageAgendaPage = lazy(() => import("@/pages/interne/PilotageAgendaPage"));
const ModuleSouscriptionAgencePage = lazy(() => import("@/pages/interne/ModuleSouscriptionAgencePage"));
const ModuleSouscriptionInternePage = lazy(() => import("@/pages/interne/ModuleSouscriptionInternePage"));
const FormalitesPage = lazy(() => import("@/pages/interne/FormalitesPage"));
const AssistancesPage = lazy(() => import("@/pages/interne/AssistancesPage"));
const FaqPilotagePage = lazy(() => import("@/pages/interne/FaqPilotagePage"));
const DocumentsPilotagePage = lazy(() => import("@/pages/interne/DocumentsPilotagePage"));

function ChargementPage() {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3" data-testid="chargement-page">
      <Loader2 className="h-8 w-8 animate-spin text-[#2b46c4]" />
      <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-400">Chargement</p>
    </div>
  );
}

function PageGuard({ page, children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <p className="flex items-center justify-center gap-2 py-20 text-sm text-slate-500" data-testid="interne-guard-loading">
        <Loader2 className="h-5 w-5 animate-spin" /> Chargement…
      </p>
    );
  }
  if (user === false) return <Navigate to="/interne" replace />;
  const pages = user?.pages;
  if (Array.isArray(pages) && pages.length > 0 && !pages.includes(page)) {
    return <Navigate to="/interne" replace />;
  }
  return children;
}

function PreEnregistrementPage() {
  return (
    <>
      <SiteHeader />
      <main className="mx-auto w-full max-w-3xl px-4 pt-10 pb-16 sm:px-6 lg:pt-12">
        <PreRegistrationForm />
      </main>
      <SiteFooter />
    </>
  );
}

function InterneArea() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="interne-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#1e2f55]" />
      </div>
    );
  }
  if (!user) {
    return (
      <>
        <header className="sticky top-0 z-20 bg-white shadow-[0_1px_0_rgba(15,23,42,0.06),0_8px_24px_rgba(15,23,42,0.06)]" data-testid="interne-header-connexion">
          <div className="tricolor-bar" />
          <div className="mx-auto flex max-w-3xl flex-col items-center px-6 py-5 text-center">
            <p className="brand-logo text-[26px] sm:text-[30px]" data-testid="interne-header-brand">
              <span className="text-[#2b46c4]">PASS</span>
              <span className="text-[#101a33]">PRO</span>
              <span className="text-[#e0312e]">FRANCE</span>
            </p>
            <p className="mt-1 text-[10.5px] font-medium uppercase tracking-[0.3em] text-slate-400" data-testid="interne-header-tagline">
              France Administrative
            </p>
          </div>
        </header>
        <main className="px-4 pb-10 pt-6">
          <Login />
        </main>
        <SiteFooter variant="interne" />
      </>
    );
  }
  return <InternalLayout />;
}

/* Titre de l'onglet navigateur — cohérent page par page, selon le nom d'origine de chaque page.
   La page d'atterrissage porte seule le nom de la marque. */
const TITRES_PAGES = {
  "/": "Passpro-France",
  "/pre-enregistrement": "Pré-enregistrement - Passpro-France",
  "/souscription": "Souscription en ligne - Passpro-France",
  "/contact": "Formulaire de contact - Passpro-France",
  "/faq": "Foire aux questions (FAQ) - Passpro-France",
  "/politique-de-confidentialite": "Politique de confidentialité - Passpro-France",
  "/mentions-legales": "Mentions légales - Passpro-France",
  "/cgv": "Conditions Générales Contractuelles - Passpro-France",
  "/agences": "Nos agences - Passpro-France",
  "/bibliotheque": "Bibliothèque de documents - Passpro-France",
  "/interne": "Espace interne - Passpro-France",
  "/interne/pre-enregistrements": "Pré-enregistrements - Passpro-France",
  "/interne/souscriptions": "Souscriptions en ligne - Passpro-France",
  "/interne/souscriptions-agence": "Souscriptions validées - Passpro-France",
  "/interne/sites-vente": "Sites de vente - Passpro-France",
  "/interne/pilotage-faq": "Pilotage FAQ - Passpro-France",
  "/interne/pilotage-documents": "Pilotage des documents - Passpro-France",
  "/interne/contacts": "Base de données - Passpro-France",
  "/interne/centre-demandes": "Centre de demandes - Passpro-France",
  "/interne/codes-envoyes": "Codes de validation - Passpro-France",
  "/interne/codes-promo": "Codes promo - Passpro-France",
  "/interne/utilisateurs": "Utilisateurs - Passpro-France",
  "/interne/pilotage-agenda": "Pilotage de l'agenda - Passpro-France",
  "/interne/module-souscription-agence": "Module souscription agence - Passpro-France",
  "/interne/module-souscription-interne": "Module souscription interne - Passpro-France",
  "/interne/centre-formalites": "Centre des formalités - Passpro-France",
  "/interne/centre-assistance": "Centre d'assistance - Passpro-France",
};

function TitreDocument() {
  const { pathname } = useLocation();
  useEffect(() => {
    document.title = TITRES_PAGES[pathname] || "Passpro-France";
  }, [pathname]);
  return null;
}

/* Remonte systématiquement en haut de page à chaque navigation (les raccourcis du pied de
   page comme les liens internes ouvrent la page cible à son début, jamais scrollée au milieu). */
function RetourHautNavigation() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <TitreDocument />
        <RetourHautNavigation />
        <div className="min-h-screen bg-white">
          <Suspense fallback={<ChargementPage />}>
          <Routes>
            <Route path="/" element={<AccueilPage />} />
            <Route path="/pre-enregistrement" element={<PreEnregistrementPage />} />
            <Route path="/souscription" element={<SouscriptionPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/politique-de-confidentialite" element={<PolitiqueConfidentialitePage />} />
            <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
            <Route path="/cgv" element={<CgvPage />} />
            <Route path="/agences" element={<AgencesPage />} />
            <Route path="/bibliotheque" element={<BibliothequePage />} />
            <Route path="/codes-promo" element={<Navigate to="/interne/codes-promo" replace />} />
            <Route path="/interne" element={<InterneArea />}>
              <Route index element={<Dashboard />} />
              <Route path="pre-enregistrements" element={<PageGuard page="pre-enregistrements"><PreEnregistrementsPage /></PageGuard>} />
              <Route path="souscriptions" element={<PageGuard page="souscriptions"><SouscriptionsSuivi /></PageGuard>} />
              <Route path="souscriptions-agence" element={<PageGuard page="souscriptions-agence"><SouscriptionsAgencePage /></PageGuard>} />
              <Route path="sites-vente" element={<PageGuard page="sites-vente"><SitesVentePage /></PageGuard>} />
              <Route path="pilotage-faq" element={<PageGuard page="pilotage-faq"><FaqPilotagePage /></PageGuard>} />
              <Route path="pilotage-documents" element={<PageGuard page="pilotage-documents"><DocumentsPilotagePage /></PageGuard>} />
              <Route path="contacts" element={<PageGuard page="contacts"><ContactsPage /></PageGuard>} />
              <Route path="agenda-rdv-clientele" element={<PageGuard page="agenda-rdv-clientele"><AgendaRdvClientelePage /></PageGuard>} />
              <Route path="comptes-clientele" element={<PageGuard page="comptes-clientele"><ComptesClientelePage /></PageGuard>} />
              <Route path="centre-demandes" element={<PageGuard page="centre-demandes"><CentreDemandesPage /></PageGuard>} />
              <Route path="codes-envoyes" element={<PageGuard page="codes-envoyes"><CodesEnvoyesPage /></PageGuard>} />
              <Route path="codes-promo" element={<PageGuard page="codes-promo"><CodesPromoPage /></PageGuard>} />
              <Route path="utilisateurs" element={<PageGuard page="utilisateurs"><UtilisateursPage /></PageGuard>} />
              <Route path="pilotage-agenda" element={<PageGuard page="pilotage-agenda"><PilotageAgendaPage /></PageGuard>} />
              <Route path="module-souscription-agence" element={<ModuleSouscriptionAgencePage />} />
              <Route path="module-souscription-interne" element={<ModuleSouscriptionInternePage />} />
              <Route path="centre-formalites" element={<PageGuard page="centre-formalites"><FormalitesPage /></PageGuard>} />
              <Route path="centre-assistance" element={<PageGuard page="centre-assistance"><AssistancesPage /></PageGuard>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
          <BandeauCookies />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}
